// זהות (01): מופע, משתמש, audit, טוקנים.
import { z } from 'zod';
import { Id, Json, Role, Ts, rowBase } from './common.ts';

export const InstanceStatus = z.enum(['active', 'hot', 'suspended']);
export type InstanceStatus = z.infer<typeof InstanceStatus>;

/** sys.instance — `config.ai3` דורס env (07). */
export const Instance = z.object({
  ...rowBase,
  slug: z.string().regex(/^[a-z][a-z0-9_]{1,30}$/),
  nameHe: z.string(),
  hostname: z.string().nullable(),
  status: InstanceStatus,
  config: z.record(z.string(), z.unknown()),
  /** platform-mfa-totp: מדיניות אפליקציית האימות; mfaRequiredAt — מתי הופעלה החובה (null כשכבויה). */
  mfa: z.object({ required: z.boolean(), graceDays: z.number().int() }),
  mfaRequiredAt: Ts.nullable(),
});
export type Instance = z.infer<typeof Instance>;

export const UserStatus = z.enum(['active', 'disabled']);
export type UserStatus = z.infer<typeof UserStatus>;

/** sys.user — `id` = `sub` של GoTrue. */
export const User = z.object({
  ...rowBase,
  email: z.email(),
  nameHe: z.string(),
  role: Role,
  pgRole: z.string().regex(/^u_[0-9a-f]{32}$/),
  status: UserStatus,
  /** users-delegation: מי יצר (admin / בעל האצלה) — מידע בלבד, לא מקנה סמכות; NULL למשתמשים מלפני 0035 ואחרי מחיקת היוצר. */
  createdBy: Id.nullable(),
  /** platform-push-notifications: סוג → false = כבוי; NULL = הכול פעיל. */
  pushPrefs: z.record(z.string(), z.boolean()).nullable(),
});
export type User = z.infer<typeof User>;

/** platform-push-notifications: סוגי ההתראות (כל אחד ניתן לכיבוי לכל משתמש). */
export const PushKind = z.enum(['turn', 'consent', 'request', 'trigger', 'share']);
export type PushKind = z.infer<typeof PushKind>;

/** sys.push_subscription — token של FCM לסשן אחד (session_id של GoTrue). */
export const PushSubscription = z.object({
  ...rowBase,
  userId: Id,
  sessionId: Id,
  token: z.string().min(20).max(4096),
  ua: z.string().max(300).nullable(),
  lastOkAt: Ts.nullable(),
});
export type PushSubscription = z.infer<typeof PushSubscription>;

/** sys.direct_link (platform-direct-link, 0052): לינק ישיר — sha256 של ה-token_hash (לא ה-token), session_id נקשר בשימוש. */
export const DirectLink = z.object({
  ...rowBase,
  userId: Id,
  tokenSha256: z.string().regex(/^[0-9a-f]{64}$/),
  expiresAt: Ts,
  usedAt: Ts.nullable(),
  sessionId: Id.nullable(),
});
export type DirectLink = z.infer<typeof DirectLink>;

/** sys.user_data_access (data-access-rls, 0046): all | restricted לכל משתמש; בלי שורה = all. admin תמיד all. */
export const UserDataAccess = z.object({
  ...rowBase,
  userId: Id,
  mode: z.enum(['all', 'restricted']),
  updatedBy: Id.nullable(),
});
export type UserDataAccess = z.infer<typeof UserDataAccess>;

/** sys.user_data_rule (data-access-rls): rows = ערכי ממד מותרים לישות; deny_entity / deny_column. */
export const UserDataRule = z.object({
  ...rowBase,
  userId: Id,
  worldKey: z.string(),
  entity: z.string(),
  kind: z.enum(['rows', 'deny_entity', 'deny_column']),
  dimension: z.string().nullable(),
  values: z.array(z.string()).nullable(),
  column: z.string().nullable(),
});
export type UserDataRule = z.infer<typeof UserDataRule>;

export const UserChangeVia = z.enum(['screen', 'aibi', 'admin_ai', 'ops']);
export type UserChangeVia = z.infer<typeof UserChangeVia>;
export const UserChangeAction = z.enum(['create', 'setRole', 'deactivate', 'reactivate', 'grant', 'revoke', 'grantScope', 'revokeScope', 'share', 'unshare', 'loginLink', 'setDelegation', 'delete', 'dataAccess', 'mfaReset']);
export type UserChangeAction = z.infer<typeof UserChangeAction>;

/** sys.user_change_log (users-delegation) — append-only; בלי סיסמה ובלי token. actor/target בלי FK (נשארים אחרי מחיקה). */
export const UserChangeLog = z.object({
  id: Id,
  instanceId: Id,
  createdAt: Ts,
  actorUserId: Id.nullable(),
  actorRole: z.enum(['admin', 'creator', 'member', 'ops']),
  actorDelegate: z.boolean(),
  via: UserChangeVia,
  targetUserId: Id.nullable(),
  targetEmail: z.string(),
  action: UserChangeAction,
  before: Json.nullable(),
  after: Json.nullable(),
  consentId: Id.nullable(),
});
export type UserChangeLog = z.infer<typeof UserChangeLog>;

export const AuditAction = z.enum(['insert', 'update', 'delete']);
export type AuditAction = z.infer<typeof AuditAction>;

/** sys.audit — נכתב ע"י trigger בלבד. `rowKey` לטבלאות שה-PK שלהן אינו uuid. */
export const Audit = z.object({
  ...rowBase,
  actorUserId: Id.nullable(),
  tableName: z.string(),
  rowId: Id.nullable(),
  rowKey: z.string().nullable(),
  action: AuditAction,
  before: Json.nullable(),
  after: Json.nullable(),
});
export type Audit = z.infer<typeof Audit>;

/** turn token (10): שיחה, 15 דק', RS256 ב-AIBIFY_RUNTIME_JWK. */
export const TurnTokenClaims = z.object({
  aud: z.literal('aibify-turn'),
  sub: Id,
  runAs: Id,
  conversationId: Id,
  /** התור החי שה-token הונפק לו — api מקבל את ה-token רק כל עוד זה התור החי של השיחה. */
  turnId: Id,
  iat: z.number().optional(),
  exp: z.number().optional(),
  iss: z.string().optional(),
});
export type TurnTokenClaims = z.infer<typeof TurnTokenClaims>;

/** run token (09): ריצת יצירה; sub = actor, runAs = בשם מי. */
export const RunTokenClaims = z.object({
  aud: z.literal('aibify-run'),
  sub: Id,
  runAs: Id,
  runId: Id,
  iat: z.number().optional(),
  exp: z.number().optional(),
  iss: z.string().optional(),
});
export type RunTokenClaims = z.infer<typeof RunTokenClaims>;

/**
 * call token (16): קריאה אחת מבלוק app (ab.call) — sub = הקורא, runAs, הריצה שבנתה את המסך, הקריאה; TTL = timeout + 30s.
 * artifacts-report-lookup: runId null = חיפוש בטופס (lookup) בלי ריצה — בלי מקור בשער, ולכן בלי ממשקים.
 */
export const CallTokenClaims = z.object({
  aud: z.literal('aibify-call'),
  sub: Id,
  runAs: Id,
  runId: Id.nullable(),
  callId: Id,
  artifactId: Id,
  version: z.number().int().positive(),
  iat: z.number().optional(),
  exp: z.number().optional(),
  iss: z.string().optional(),
});
export type CallTokenClaims = z.infer<typeof CallTokenClaims>;


/** platform-mfa-totp: מדיניות אפליקציית אימות למופע (sys.instance.mfa). required → כל משתמש חייב גורם מאומת; graceDays — ימי חסד מרגע ההפעלה (או מיצירת המשתמש, המאוחר). */
export const MfaPolicy = z.object({ required: z.boolean(), graceDays: z.number().int().min(0).max(365) });
export type MfaPolicy = z.infer<typeof MfaPolicy>;
export const MFA_POLICY_DEFAULT: MfaPolicy = { required: false, graceDays: 7 };

/** platform-mfa-totp: מה הסשן הנוכחי צריך לעשות (GET /api/auth/mfa/status). verify — יש גורם, הסשן עדיין aal1; enroll — חובה ועבר החסד; enroll_optional — חובה ובתוך החסד. */
export const MfaNeed = z.enum(['none', 'verify', 'enroll', 'enroll_optional']);
export type MfaNeed = z.infer<typeof MfaNeed>;
export const MfaStatus = z.object({
  aal: z.enum(['aal1', 'aal2']),
  enrolled: z.boolean(),
  /** מזהה הגורם המאומת (לאתגר ב-GoTrue) — רק כשיש */
  factorId: z.string().nullable(),
  need: MfaNeed,
  policy: MfaPolicy,
  /** סוף ימי החסד (ISO) — רק כשהמדיניות חובה והמשתמש בלי גורם */
  graceUntil: z.string().nullable(),
});
export type MfaStatus = z.infer<typeof MfaStatus>;
